import React, { useState, useEffect, useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMachine } from "@xstate/react";
import { createMachine } from "xstate";
import { LoadingPanel } from "../LoadingPanel/LoadingPanel";
import { reloadOptions } from "../../request/api";
import { saveAuthentication } from "../../helpers/token";
import { sendLoadingPostRequest } from "../../request/request";
import { IStep1FormInput } from "../../types/IStep1FormInput";
import { createTheme, styled, TextField, ThemeProvider } from "@mui/material";
import FormContext from "../../contexts/FormContext";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import pl from "../../helpers/pl.json";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { Dictionary } from "../../types/Dictionary";
import { onlyFirstCapitalize, toTitleCase } from "../../helpers/string";
import { sanitizeValue } from "../../helpers/inputSanitizer";

interface IStep1Form {
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

interface IStep1Confirm {
  confirmEmailCode: string;
}

const toggleMachine = createMachine({
  id: "toggle",
  initial: "initial",
  states: {
    initial: {
      on: { TOGGLE: "confirmEmail" },
    },
    confirmEmail: {
      on: { TOGGLE: "initial" },
    },
  },
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root .MuiAutocomplete-input:first-child": {
    height: 30,
  },

  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    transform: "translate(14px, -6px) scale(0.75) !important",
  },
  "& .MuiInputLabel-root": {
    transform: "translate(14px, 20px) scale(1)",
  },
}));

const defaultMaterialTheme = createTheme({
  palette: {
    primary: {
      main: "#000000",
    },
  },
  components: {
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          display: "none",
        },
      },
    },
  },
});

const Step1Form = ({ step, setStep }: IStep1Form) => {
  const context = useContext(FormContext);
  const { height, width } = useWindowDimensions();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, touchedFields },
    reset,
    getValues,
    setValue,
    clearErrors,
    setError,
  } = useForm<IStep1FormInput>({
    defaultValues: context.step1,
    mode: "onChange",
    shouldFocusError: false,
  });
  const [state, send] = useMachine(toggleMachine);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState<boolean>(false);
  const [format, setFormat] = useState<RegExp>();
  const [names, setName] = useState<string[]>([]);
  const [counter, setCounter] = useState<number>(1);

  const { phone } = getValues();

  useEffect(() => {
    console.log(errors, "@@@");
    const errorsKeys = Object.keys(errors);
    if (errorsKeys != null) {
      for (let i = 0; i < names.length; i++) {
        const currName = names[i];
        if (errorsKeys.indexOf(currName) > -1) {
          const firstErrorElement = document.getElementsByName(currName)[0];
          firstErrorElement.scrollIntoView({
            behavior: `smooth`,
            block: "center",
          });
          break;
        }
      }
    }
  }, [names, errors]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getDictionary = async () => {
    reloadOptions();
    const { data: response, success } = await sendLoadingPostRequest<
      never,
      Dictionary
    >({
      loadingFunction: setLoading,
      endpoint: "Dictionary/GetAll",
    });
    if (success && response?.data) {
      if (context.step2 == null) {
        const poland = response?.data.countries.filter(
          (item) => item.name?.toLocaleLowerCase() === "polska",
        )[0];
        const identification = response.data.identityDocumentTypes.filter(
          (item) => item.name === "Dowód osobisty",
        )[0];
        if (context.setStep2) {
          context.setStep2({
            countryOfBirth: poland,
            address: {
              country: poland,
              fatherName: "",
              motherName: "",
              postCode: "",
              city: "",
              street: "",
              house: "",
              apartment: "",
            },
            identification: {
              issuingCountry: poland,
              type: identification,
              seriesAndNumber: "",
              withoutExpirationDate: false,
            },
            pesel: "",
            withoutPesel: false,
            secondName: "",
            placeOfBirth: "",
            citizenship: poland,
            mothersMaidenName: "",
            profession: undefined,
            otherPostalAddress: false,
            identificationPhotos: {
              mainSide: undefined,
              backSide: undefined,
            },
            secondIdentificationPhotos: {
              mainSide: undefined,
              backSide: undefined,
            },
          });
        }
      }
    }
  };

  const onSubmit = async (data: IStep1FormInput) => {
    if (state.value === "confirmEmail") {
      const { data: confirmResponse, success } = await sendLoadingPostRequest<
        IStep1Confirm,
        boolean
      >({
        loadingFunction: setLoading,
        endpoint: "Agreement/Step1Confirm",
        params: {
          confirmEmailCode: data.confirmEmailCode,
        },
      });
      await sendLeadsUpdate(data);
      if (success && confirmResponse?.data) {
        await getDictionary();
        setStep(3);
        if (context.setStep1) context.setStep1(data);
      } else {
        setErrorCode(true);
      }
    } else {
      if (format && phone.match(format) == null) return;
      const { data: response, success } = await sendLoadingPostRequest<
        IStep1FormInput,
        string
      >({
        loadingFunction: setLoading,
        endpoint: "Agreement/Step1",
        params: {
          ...data,
          leadsGuid: context.leads?.guid,
        },
      });
      if (success && response) {
        saveAuthentication(response?.data);
        reloadOptions();
        reset({ ...getValues(), confirmEmailCode: undefined });
        setStep2();
      }
    }
  };

  const sendLeadsUpdate = async (data: IStep1FormInput) => {
    const { leads } = context;
    const leadsData = leads?.data;
    if (leadsData) {
      if (
        leadsData.firstName !== data.firstName ||
        leadsData.lastName !== data.lastName ||
        leadsData.email !== data.mail ||
        leadsData.phone !== data.phone
      ) {
        await sendLoadingPostRequest({
          loadingFunction: setLoading,
          endpoint: "Leads/UpdateLeadBasicDetails",
          params: {
            firstName: data.firstName,
            lastName: data.lastName,
            mail: data.mail,
            phone: data.phone,
            guid: leads?.guid,
          },
        });
      }
    }
  };

  const setStep2 = () => {
    send({ type: "TOGGLE" });
    setStep(2);
    setLoading(false);
  };

  const onBack = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    reset({ ...getValues(), confirmEmailCode: undefined });
    send({ type: "TOGGLE" });
    setStep(1);
  };

  const onResendCode = async () => {
    const { success } = await sendLoadingPostRequest({
      loadingFunction: setLoading,
      endpoint: "Agreement/ResendCode",
    });
    if (success) {
      reset({ ...getValues(), confirmEmailCode: undefined });
      setCounter((prev) => prev + 1);
    }
  };

  const setCheckbox = (e: any) => {
    if (e.target.getAttribute("class") !== "link") {
      const { dataProcessing } = getValues();
      if (!dataProcessing) {
        clearErrors("dataProcessing");
      }
      setValue("dataProcessing", !dataProcessing);
    }
  };

  const setDataConfirmCheckbox = () => {
    const { dataConfirm } = getValues();
    if (!dataConfirm) {
      clearErrors("dataConfirm");
    }
    setValue("dataConfirm", !dataConfirm);
  };

  const handleOnSubmit = (event: any) => {
    const form = Array.from(event?.target);
    const names = form
      .map((item: any) => item.name)
      .filter((name: string) => name !== "");
    setName(names);
    handleSubmit(onSubmit)(event);
  };

  useEffect(() => {
    if (state.value === "confirmEmail") {
      var cols = document.getElementsByClassName(
        "special-label",
      ) as HTMLCollectionOf<HTMLElement>;
      for (var i = 0; i < cols.length; i++) {
        cols[i].style.color = "rgba(0, 0, 0, 0.3)";
      }
    } else {
      var cols = document.getElementsByClassName(
        "special-label",
      ) as HTMLCollectionOf<HTMLElement>;
      for (var i = 0; i < cols.length; i++) {
        cols[i].style.color = "rgba(0, 0, 0, 1)";
      }
    }
  }, [state]);

  return (
    <ThemeProvider theme={defaultMaterialTheme}>
      <form
        onKeyPress={(e) => {
          e.key === "Enter" && e.preventDefault();
        }}
        onSubmit={handleOnSubmit}
      >
        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Controller
              render={({ field }) => (
                <StyledTextField
                  ref={field.ref}
                  disabled={state.value === "confirmEmail"}
                  label="Imię"
                  name="firstName"
                  error={errors.firstName != null}
                  variant="outlined"
                  value={field.value}
                  onChange={(event: any) => {
                    field.onChange(event.target.value);
                  }}
                  onBlur={(event: any) => {
                    if (event.target.value)
                      field.onChange(onlyFirstCapitalize(event.target.value));
                  }}
                  inputProps={{
                    maxLength: 50,
                  }}
                />
              )}
              name="firstName"
              control={control}
              rules={{
                required: "Imię jest wymagane",
                minLength: {
                  value: 2,
                  message: "Imię jest za krótkie",
                },
                maxLength: {
                  value: 50,
                  message: "Imię musi być krótsze niż 50 znaków",
                },
                validate: {
                  invalidChars: (value) =>
                    !value ||
                    /^[a-zA-ZąćęłńóśźżĆĄĘŁŃÓŚŹŻ\s'-]+$/.test(value) ||
                    "Imię zawiera niedozwolone znaki",
                },
              }}
            />
            {errors.firstName && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {errors.firstName.message}
              </span>
            )}
          </div>
        </div>
        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Controller
              render={({ field }) => (
                <StyledTextField
                  ref={field.ref}
                  disabled={state.value === "confirmEmail"}
                  label="Nazwisko"
                  error={errors.lastName != null}
                  name="lastName"
                  variant="outlined"
                  value={field.value}
                  onChange={(event: any) => {
                    field.onChange(event.target.value);
                  }}
                  onBlur={(event: any) => {
                    if (event.target.value)
                      field.onChange(toTitleCase(event.target.value));
                  }}
                  inputProps={{
                    maxLength: 200,
                  }}
                />
              )}
              name="lastName"
              control={control}
              rules={{
                required: "Nazwisko jest wymagane",
                minLength: {
                  value: 2,
                  message: "Nazwisko jest za krótkie",
                },
                maxLength: {
                  value: 200,
                  message: "Nazwisko musi być krótsze niż 200 znaków",
                },
                validate: {
                  invalidChars: (value) =>
                    !value ||
                    /^[a-zA-ZąćęłńóśźżĆĄĘŁŃÓŚŹŻ\s'-]+$/.test(value) ||
                    "Nazwisko zawiera niedozwolone znaki",
                },
              }}
            />
            {errors.lastName && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {errors.lastName.message}
              </span>
            )}
          </div>
        </div>
        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Controller
              render={({ field }) => (
                <PhoneInput
                  key={"xxx"}
                  disabled={state.value === "confirmEmail"}
                  inputClass={`form-control phone ${errors.phone != null ||
                    (format && phone.match(format) == null)
                    ? "phoneRed"
                    : ""
                    } ${state.value === "confirmEmail" ? "phoneGrey" : "phoneBlack"
                    }`}
                  country={"pl"}
                  preferredCountries={["pl"]}
                  localization={pl}
                  value={field.value}
                  isValid={
                    errors.phone != null ||
                    (format && phone.match(format) != null)
                  }
                  onChange={(value, data: any, event, formattedValue) => {
                    setFormat(
                      new RegExp(
                        data.format
                          .replaceAll(".", "[0-9]")
                          .replace(/[.*+?^${}()|\\]/g, "\\$&"),
                        "g",
                      ),
                    );
                    field.onChange(formattedValue);
                  }}
                  placeholder="Telefon"
                  specialLabel="Telefon"
                  inputProps={{
                    name: "phone",
                  }}
                />
              )}
              name="phone"
              control={control}
              rules={{
                required: "Telefon jest wymagany",
                minLength: {
                  value: 1,
                  message: "Telefon jest wymagany",
                },
              }}
            />
            {errors.phone ? (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {errors.phone.message}
              </span>
            ) : (
              <>
                {format && phone.match(format) == null && (
                  <span className="validationError" role="alert">
                    <i className="fa fa-exclamation-circle" />
                    Nieprawidłowy numer telefonu
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Controller
              render={({ field }) => (
                <StyledTextField
                  ref={field.ref}
                  disabled={state.value === "confirmEmail"}
                  label="Adres e-mail"
                  name="mail"
                  variant="outlined"
                  error={errors.mail != null}
                  value={field.value}
                  onChange={(event: any) => {
                    field.onChange(event.target.value);
                  }}
                  inputProps={{
                    maxLength: 100,
                  }}
                />
              )}
              name="mail"
              control={control}
              rules={{
                required: "Adres e-mail jest wymagany",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Nieprawidłowy adres e-mail",
                },
                maxLength: {
                  value: 100,
                  message: "E-mail jest za długi",
                },
              }}
            />
            {errors.mail && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {errors.mail.message}
              </span>
            )}
          </div>
        </div>
        <div className="row px-3 mt-4 mb-1">
          <div
            onClick={(e) => setCheckbox(e)}
            className="custom-control custom-checkbox"
          >
            <input
              readOnly={state.value === "confirmEmail"}
              {...register("dataProcessing", {
                required: "Zaznaczenie oświadczenia jest wymagane.",
              })}
              id="dataProcessing"
              type="checkbox"
              className="custom-control-input"
            />
            <label className="custom-control-label">
              Zapoznałam/em się z treścią{" "}
              <a
                className="link red"
                href="https://www.xelion.pl/regulaminy-i-informacje"
                target="_blank"
              >
                {" "}
                Informacji administratora danych{" "}
              </a>
              oraz{" "}
              <a
                className="link red"
                href="https://www.xelion.pl/wykorzystanie-cookies"
                target="_blank"
              >
                Polityką plików cookies
              </a>{" "}
              i zostałam/em poinformowana/y, że administratorem moich danych
              osobowych jest Dom Inwestycyjny Xelion sp. z o.o. z siedzibą w
              Warszawie.{" "}
            </label>
          </div>
        </div>
        <div>
          {errors.dataProcessing && (
            <span className="validationError" role="alert">
              <i className="fa fa-exclamation-circle" />
              {errors.dataProcessing.message}
            </span>
          )}
        </div>
        <div className="row px-3 mt-2 mb-1">
          <div
            onClick={setDataConfirmCheckbox}
            className="custom-control custom-checkbox"
          >
            <input
              readOnly={state.value === "confirmEmail"}
              {...register("dataConfirm", {
                required: "Zaznaczenie oświadczenia jest wymagane.",
              })}
              id="dataConfirm"
              type="checkbox"
              className="custom-control-input"
            />
            <label className="custom-control-label">
              Oświadczam, że podane przeze mnie dane osobowe na potrzeby
              zawarcia Umowy Dostępu są prawdziwe i aktualne.
            </label>
          </div>
        </div>
        <div>
          {errors.dataConfirm && (
            <span className="validationError" role="alert">
              <i className="fa fa-exclamation-circle" />
              {errors.dataConfirm.message}
            </span>
          )}
        </div>

        <div className="row px-3 mt-3 mb-1 bold">
          Zaznaczenie powyższych oświadczeń jest wymagane.
        </div>

        <div
          className={`${state.value === "confirmEmail"
            ? "confirmEmailShow"
            : "confirmEmailHide"
            }`}
        >
          {state.value === "confirmEmail" ? (
            <>
              <div className="codeContainer mt-3 row">
                <div className="code" style={{ width: "160px" }}>
                  <div className="form-group mt-1 mb-1">
                    <Controller
                      render={({ field }) => (
                        <StyledTextField
                          label={`${width < 400 ? "Kod" : "Kod weryfikacyjny"}`}
                          variant="outlined"
                          value={field.value}
                          error={errors.confirmEmailCode != null || errorCode}
                          inputProps={{
                            maxLength: 6,
                          }}
                          onChange={(event: any) => {
                            field.onChange(event.target.value);
                            setErrorCode(false);
                          }}
                        />
                      )}
                      name="confirmEmailCode"
                      control={control}
                      rules={{
                        required: "Kod weryfikacyjny jest wymagany",
                      }}
                    />
                  </div>
                </div>

                <div className="col-2 codeBtn">
                  <div className="mt-3 mb-1 marginButton">
                    <input className="nextBtn" type="submit" value="Dalej" />
                  </div>
                </div>
                <div className="col-12 code">
                  {errors.confirmEmailCode && (
                    <p className="validationError" role="alert">
                      <i className="fa fa-exclamation-circle" />
                      {errors.confirmEmailCode.message}
                    </p>
                  )}
                  {errorCode && (
                    <p className="validationError" role="alert">
                      <i className="fa fa-exclamation-circle" />
                      Podano błędny kod
                    </p>
                  )}
                </div>
              </div>

              <div className="row px-3 mt-3 mb-1">
                Aby kontynuować należy w polu powyżej wpisać kod weryfikacyjny
                nr {counter} otrzymany na wskazany adres e-mail. Jeśli kod nie
                dotarł należy sprawdzić folder SPAM w skrzynce pocztowej lub
                kliknąć {width <= 350 ? "Pobierz kod" : "Pobierz ponownie kod"}
              </div>
            </>
          ) : null}
        </div>

        <div className="row px-3 mb-3 mt-4">
          <div className="col-sm-12 code">
            <div className="mr-12 float-right">
              {state.value === "confirmEmail" ? (
                <>
                  {width <= 321 ? (
                    <>
                      <button
                        onClick={onBack}
                        className="backBtn"
                        type="submit"
                      >
                        Cofnij
                      </button>
                      <span onClick={onResendCode} className="sendBtn">
                        Pobierz kod
                      </span>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={onBack}
                        className="backBtn"
                        type="submit"
                      >
                        Cofnij
                      </button>
                      <span onClick={onResendCode} className="sendBtn">
                        Pobierz ponownie kod
                      </span>
                    </>
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={() => setStep(0)}
                    className="backBtn"
                    type="submit"
                  >
                    Cofnij
                  </button>
                  <input className="nextBtn" type="submit" value="Dalej" />
                </>
              )}
            </div>
          </div>
        </div>
      </form>
      {loading && <LoadingPanel />}
    </ThemeProvider>
  );
};

export { Step1Form };
